import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trophy } from "lucide-react";
import { api } from "../lib/api";
import { getSocket } from "../lib/socket";
import {
  LeaderboardList,
  type LeaderboardEntry,
} from "../components/storefront/LeaderboardTeaser";
import { PageHeader } from "../components/storefront/PageHeader";
import { EmptyState, PageLoader } from "../components/ui";

type Period = "7d" | "30d" | "all";

const PERIODS: { id: Period; label: string }[] = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "all", label: "All time" },
];

export function Leaderboard() {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<Period>("7d");

  const { data, isLoading } = useQuery({
    queryKey: ["pack-leaderboard", period],
    queryFn: () => api<{ entries: LeaderboardEntry[] }>(`/packs/leaderboard?period=${period}&limit=20`),
  });

  useEffect(() => {
    const socket = getSocket();
    const onPull = () => {
      void queryClient.invalidateQueries({ queryKey: ["pack-leaderboard"] });
    };
    socket.on("feed:pack-pull", onPull);
    return () => {
      socket.off("feed:pack-pull", onPull);
    };
  }, [queryClient]);

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-5 px-4 pt-4 lg:px-6">
      <PageHeader
        title="Pack rip leaderboard"
        description="Most pack opens win the top spots — rankings update live as collectors rip."
      />

      <div className="flex gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPeriod(p.id)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
              period === p.id
                ? "bg-gradient-brand text-white glow-brand"
                : "border border-white/10 bg-surface text-white/60 hover:border-brand-pink/30"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {data?.entries.length ? (
        <LeaderboardList entries={data.entries} />
      ) : (
        <EmptyState icon={Trophy} title="No rips yet" hint="Open a pack to climb the board." />
      )}
    </div>
  );
}
